import { Check, ArrowUpRight } from "lucide-react";
import { Reveal, Btn, Overline, MaskedLine } from "@/components/motion-bits";
import regionalImg from "@/assets/service_regional.jpg";
import otrImg from "@/assets/service_otr.jpg";

const REGIONAL_POINTS = [
  "Dedicated regional lanes radiating out of Memphis",
  "Home every weekend — no guessing games",
  "Regular freight, regular routes, regular rest",
  "The same dispatch team every time you call",
];

const OTR_POINTS = [
  "Over-the-road freight running across the country",
  "Two weeks out, then three full days home",
  "Miles that add up on a schedule you can plan around",
  "Dispatch that answers when you call",
];

const CheckRow = ({ children, light = false }) => (
  <li className="flex items-start gap-3">
    <span className={`mt-1 inline-flex h-6 w-6 shrink-0 items-center justify-center border-2 ${light ? "border-ink bg-brand-gold text-ink" : "border-ink bg-brand-gold text-ink"}`}>
      <Check size={14} strokeWidth={3.5} aria-hidden="true" />
    </span>
    <span className={`leading-relaxed ${light ? "text-cream/85" : "text-ink/80"}`}>{children}</span>
  </li>
);

const Services = () => (
  <div data-testid="services-page">
    {/* Hero */}
    <section className="px-6 lg:px-10 py-16 md:py-24">
      <div className="max-w-7xl mx-auto">
        <Overline className="text-brand-red">
          <MaskedLine delay={0.1}>Services</MaskedLine>
        </Overline>
        <h1 className="mt-4 font-display uppercase tracking-tight leading-[0.9] text-[clamp(3rem,8vw,7rem)]">
          <MaskedLine delay={0.24}>The lanes</MaskedLine>
          <MaskedLine delay={0.38}>
            <span className="text-brand-teal">we run.</span>
          </MaskedLine>
        </h1>
        <Reveal delay={0.5}>
          <p className="mt-8 max-w-2xl text-lg leading-relaxed text-ink/75">
            Two ways to move with Memphis Viaduct Inc: loop the region and sleep at home on
            weekends, or put in two weeks over the road and take three full days when you're
            back. Either way, the freight leaves Memphis on time — from the I-55 &amp; I-40
            crossing point on the Mississippi River bank.
          </p>
        </Reveal>
      </div>
    </section>

    {/* Regional */}
    <section id="regional" className="px-6 lg:px-10 pb-20 md:pb-28" data-testid="services-regional-section">
      <div className="max-w-7xl mx-auto grid md:grid-cols-2 gap-10 lg:gap-16 items-center">
        <Reveal>
          <div className="-rotate-1 border-[3px] border-ink bg-cream p-2 shadow-blocklg">
            <img
              src={regionalImg}
              alt="Illustration of a regional semi truck heading toward a farmhouse at dusk"
              className="w-full aspect-square object-cover"
              data-testid="services-regional-illustration"
            />
            <p className="px-2 py-3 text-center font-display uppercase tracking-wide text-lg text-brand-red">
              Division 01 · Regional
            </p>
          </div>
        </Reveal>
        <div>
          <Reveal>
            <Overline className="text-brand-red">Division 01</Overline>
            <h2 className="mt-3 font-display uppercase tracking-tight text-4xl md:text-6xl">Regional</h2>
            <p className="mt-3 font-display uppercase tracking-tight text-2xl md:text-3xl text-brand-teal">
              Home every weekend.
            </p>
          </Reveal>
          <Reveal delay={0.12}>
            <ul className="mt-8 space-y-4">
              {REGIONAL_POINTS.map((p) => (
                <CheckRow key={p}>{p}</CheckRow>
              ))}
            </ul>
          </Reveal>
          <Reveal delay={0.2}>
            <div className="mt-10 flex flex-wrap gap-4">
              <Btn to="/careers" variant="red" testid="services-regional-apply-cta">
                Apply Regional <ArrowUpRight size={20} aria-hidden="true" />
              </Btn>
              <Btn href="tel:+18338123828" variant="cream" testid="services-regional-phone-cta">Call Dispatch</Btn>
            </div>
          </Reveal>
        </div>
      </div>
    </section>

    {/* OTR */}
    <section id="otr" className="bg-brand-tealdeep text-cream border-y-[3px] border-ink px-6 lg:px-10 py-20 md:py-28" data-testid="services-otr-section">
      <div className="max-w-7xl mx-auto grid md:grid-cols-2 gap-10 lg:gap-16 items-center">
        <div className="md:order-2">
          <Reveal>
            <div className="rotate-1 border-[3px] border-brand-gold bg-cream p-2 shadow-blocklg">
              <img
                src={otrImg}
                alt="Illustration of an over-the-road semi truck on an open interstate under a starry sky"
                className="w-full aspect-square object-cover"
                data-testid="services-otr-illustration"
              />
              <p className="px-2 py-3 text-center font-display uppercase tracking-wide text-lg text-brand-teal">
                Division 02 · Over the Road
              </p>
            </div>
          </Reveal>
        </div>
        <div className="md:order-1">
          <Reveal>
            <Overline className="text-brand-gold">Division 02</Overline>
            <h2 className="mt-3 font-display uppercase tracking-tight text-4xl md:text-6xl">Over the Road</h2>
            <p className="mt-3 font-display uppercase tracking-tight text-2xl md:text-3xl text-brand-gold">
              Two weeks out. Three days home.
            </p>
          </Reveal>
          <Reveal delay={0.12}>
            <ul className="mt-8 space-y-4">
              {OTR_POINTS.map((p) => (
                <CheckRow key={p}>{p}</CheckRow>
              ))}
            </ul>
          </Reveal>
          <Reveal delay={0.2}>
            <div className="mt-10 flex flex-wrap gap-4">
              <Btn to="/careers" variant="gold" testid="services-otr-apply-cta">
                Apply OTR <ArrowUpRight size={20} aria-hidden="true" />
              </Btn>
              <Btn href="tel:+18338123828" variant="cream" testid="services-otr-phone-cta">Call Dispatch</Btn>
            </div>
          </Reveal>
        </div>
      </div>
    </section>

    {/* Why Memphis */}
    <section className="bg-ink text-cream px-6 lg:px-10 py-20 md:py-28">
      <div className="max-w-7xl mx-auto grid md:grid-cols-12 gap-12 items-center">
        <div className="md:col-span-7">
          <Reveal>
            <Overline className="text-brand-gold">Why Memphis</Overline>
            <h2 className="mt-4 font-display uppercase tracking-tight text-4xl md:text-6xl leading-[0.95]">
              Load from the middle of the map.
            </h2>
          </Reveal>
          <Reveal delay={0.12}>
            <p className="mt-8 text-lg leading-relaxed text-cream/75 max-w-xl">
              Our base sits on the Mississippi River bank at the I-55 &amp; I-40 crossing — where
              the river's north-south flow meets the east-west interstates. When you ship with the
              Viaduct, your freight starts at a crossing point that reaches every direction, and
              moves on a schedule we keep.
            </p>
          </Reveal>
        </div>
        <Reveal delay={0.18} className="md:col-span-4 md:col-start-9">
          <div className="border-[3px] border-brand-gold p-6 md:p-8">
            <p className="font-display uppercase tracking-tight text-3xl leading-tight">
              I-55 × I-40
              <br />
              <span className="text-brand-gold">Mississippi River Bank</span>
            </p>
            <p className="mt-4 text-cream/70 leading-relaxed">
              Memphis, Tennessee — established 2025, reliable from day one.
            </p>
          </div>
        </Reveal>
      </div>
    </section>
  </div>
);

export default Services;
